/**
 * usePush — manages the full Web Push subscription lifecycle.
 * Works without auth by using a persistent device_id from localStorage.
 *
 * status: 'idle' | 'unsupported' | 'denied' | 'subscribed' | 'unsubscribed' | 'loading' | 'error'
 */

import { useState, useEffect, useCallback } from "react";
import { getUser } from "@/store";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const VAPID_PUBLIC_KEY = process.env.REACT_APP_VAPID_PUBLIC_KEY || "";

// Get or create a persistent device ID
function getDeviceId() {
  let id = localStorage.getItem("hypha_device_id");
  if (!id) {
    id = `device_${Date.now()}_${Math.random().toString(36).substr(2, 12)}`;
    localStorage.setItem("hypha_device_id", id);
  }
  return id;
}

// Convert base64url to Uint8Array (required by pushManager.subscribe)
function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
}

async function getRegistration() {
  if (!("serviceWorker" in navigator)) return null;
  return navigator.serviceWorker.ready;
}

async function apiSubscribe(subscription) {
  const deviceId = getDeviceId();
  const user = getUser();
  const notifyTime = user.notifyTime || "09:00";
  const sub = subscription.toJSON ? subscription.toJSON() : subscription;

  const res = await axios.post(`${BACKEND_URL}/api/push/subscribe`, {
    device_id: deviceId,
    endpoint: sub.endpoint,
    keys: sub.keys,
    notify_time: notifyTime,
  });
  return res.status === 201;
}

async function apiUnsubscribe() {
  const deviceId = getDeviceId();
  await axios.delete(`${BACKEND_URL}/api/push/subscribe`, {
    data: { device_id: deviceId },
  });
}

export function usePush() {
  const [status, setStatus] = useState("idle");
  const [supported, setSupported] = useState(false);

  // Initial detection
  useEffect(() => {
    if (
      !("serviceWorker" in navigator) ||
      !("PushManager" in window) ||
      !VAPID_PUBLIC_KEY
    ) {
      setStatus("unsupported");
      return;
    }
    setSupported(true);

    const perm = Notification.permission;
    if (perm === "denied") {
      setStatus("denied");
      return;
    }

    // Check if already subscribed
    getRegistration().then((reg) => {
      if (!reg) return;
      reg.pushManager.getSubscription().then((sub) => {
        setStatus(sub ? "subscribed" : "unsubscribed");
      });
    });
  }, []);

  // Listen for subscription rotation from SW
  useEffect(() => {
    const handler = async (e) => {
      if (e.data?.type === "PUSH_SUBSCRIPTION_CHANGED") {
        await apiSubscribe({ toJSON: () => e.data.subscription });
      }
    };
    navigator.serviceWorker?.addEventListener("message", handler);
    return () => navigator.serviceWorker?.removeEventListener("message", handler);
  }, []);

  // Subscribe
  const subscribe = useCallback(async () => {
    if (!supported) return { ok: false, reason: "unsupported" };
    setStatus("loading");

    const perm = await Notification.requestPermission();
    if (perm !== "granted") {
      setStatus(perm === "denied" ? "denied" : "unsubscribed");
      return { ok: false, reason: perm };
    }

    try {
      const reg = await getRegistration();
      if (!reg) throw new Error("No service worker registration.");

      // Unsubscribe from any old subscription first
      const existing = await reg.pushManager.getSubscription();
      if (existing) await existing.unsubscribe();

      // Create new subscription
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
      });

      // Register with backend
      const ok = await apiSubscribe(sub);
      setStatus(ok ? "subscribed" : "error");
      return { ok };
    } catch (err) {
      console.error("Push subscribe error:", err);
      setStatus("error");
      return { ok: false, reason: err.message };
    }
  }, [supported]);

  // Unsubscribe
  const unsubscribe = useCallback(async () => {
    setStatus("loading");
    try {
      const reg = await getRegistration();
      if (reg) {
        const sub = await reg.pushManager.getSubscription();
        if (sub) await sub.unsubscribe();
      }
      await apiUnsubscribe();
      setStatus("unsubscribed");
    } catch (err) {
      console.error("Push unsubscribe error:", err);
      setStatus("error");
    }
  }, []);

  return { status, supported, subscribe, unsubscribe };
}
